import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  createPromptPreset,
  deletePromptPreset,
  fetchPromptPresets,
  updatePromptPreset,
  type CreatePromptPresetInput,
  type PromptPreset,
  type UpdatePromptPresetInput,
} from '../../lib/api';
import { queryKeys } from './queryKeys';

export const usePromptPresets = (token: string | null, projectId: string) => {
  return useQuery({
    queryKey: queryKeys.promptPresets.list(projectId),
    queryFn: () => fetchPromptPresets(token as string, projectId, { pageSize: 100 }),
    enabled: Boolean(token) && projectId.length > 0,
    staleTime: 60_000,
    select: (result) => result.data,
  });
};

export const useCreatePromptPresetMutation = (token: string | null, projectId: string) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (input: CreatePromptPresetInput) => createPromptPreset(token as string, projectId, input),
    onMutate: async () => {
      await queryClient.cancelQueries({
        queryKey: queryKeys.promptPresets.all(projectId),
      });
      const previous = queryClient.getQueriesData({
        queryKey: queryKeys.promptPresets.lists(projectId),
      });
      return { previous };
    },
    onError: (_error, _input, context) => {
      if (context?.previous) {
        for (const [key, data] of context.previous) {
          queryClient.setQueryData(key, data);
        }
      }
    },
    onSettled: () => {
      void queryClient.invalidateQueries({
        queryKey: queryKeys.promptPresets.all(projectId),
      });
    },
  });
};

export const useUpdatePromptPresetMutation = (token: string | null, projectId: string) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, input }: { id: string; input: UpdatePromptPresetInput }) =>
      updatePromptPreset(token as string, projectId, id, input),
    onSettled: () => {
      void queryClient.invalidateQueries({
        queryKey: queryKeys.promptPresets.all(projectId),
      });
    },
  });
};

export const useDeletePromptPresetMutation = (token: string | null, projectId: string) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => deletePromptPreset(token as string, projectId, id),
    onMutate: async (id) => {
      await queryClient.cancelQueries({
        queryKey: queryKeys.promptPresets.all(projectId),
      });
      const previous = queryClient.getQueriesData({
        queryKey: queryKeys.promptPresets.lists(projectId),
      });
      queryClient.setQueriesData(
        { queryKey: queryKeys.promptPresets.lists(projectId) },
        (old: { data: PromptPreset[]; meta: unknown } | undefined) => {
          if (!old) return old;
          return {
            ...old,
            data: old.data.filter((p) => p.id !== id),
          };
        },
      );
      return { previous };
    },
    onError: (_error, _id, context) => {
      if (context?.previous) {
        for (const [key, data] of context.previous) {
          queryClient.setQueryData(key, data);
        }
      }
    },
    onSettled: () => {
      void queryClient.invalidateQueries({
        queryKey: queryKeys.promptPresets.all(projectId),
      });
    },
  });
};
